import React from "react";
import { getExperimentsClient } from "../../../services/ai-for-pfd.service";
import { Body1, Card, CardHeader, CardPreview, Spinner } from "@fluentui/react-components";
import { getFiltersClient, getThumbnailsClient } from "../../../services/dashboard.service";
import thumbnail404 from "../../../assets/images/thumbnail-404.png";
import { Alert } from "@fluentui/react-components/unstable";
import { ScenarioResultModel } from "../../../swagger-clients/excel-runner-api-clients.service";
import { getScenariosClient } from "../../../services/excel-runner.service";
import { processServerError } from "../../../utils/helpers/error.helper";
import { FilterFlowsheetQuery, FilterResultModel, ShareDefinitionSourceType } from "../../../swagger-clients/s365-dashboard-v2-api-clients.service";
import Co2AbsorptionWithMea from "../../../assets/images/examples/doe/CO2_Absorption_with_MEA.png";
import { generalSettings } from "../../../App";

type ScenarioExampleProps = {
    scenarioId: number;
    onClick: () => void;
}
type DoeExampleObject = {
    [scenarioId: string]: any;
}
type Environment = "development" | "staging" | "production";

type DoeExampleEnvironmentMapping = {
    [K in Environment]: DoeExampleObject;
};

const doeExampleMappings: DoeExampleEnvironmentMapping = {
    "development": {
        "101": Co2AbsorptionWithMea
    },
    "staging": {
        "427": Co2AbsorptionWithMea
    },
    "production": {
        "668": Co2AbsorptionWithMea
    }
}

export const ScenarioExample: React.FC<ScenarioExampleProps> = (props) => {

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [scenario, setScenario] = React.useState<ScenarioResultModel>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [image, setImage] = React.useState<string>();

    React.useEffect(() => {
        onInit();
    }, [props.scenarioId]);

    const onInit = async () => {
        setIsLoading(true);
        const experimentResp = await getExperiment();
        if (!!experimentResp) {
            const environment = generalSettings.environment ?? "development";
            const existingImage = doeExampleMappings[environment]?.[props.scenarioId.toString()];
            if (!!existingImage) {
                setImage(existingImage);
                setIsLoading(false);
                return;
            }

            const filterFileResp = await getFilterFileInfo(experimentResp.filterId, experimentResp.filterVersionId);
            if (filterFileResp)
                await getSmallThumbnail(filterFileResp.fileUniqueIdentifier, +filterFileResp.fileVersionNumber);
        }
        setIsLoading(false);
    }
    const getFilterFileInfo = async (filterId: number, filterVersionId: number) => {
        try {
            const client = getFiltersClient();
            const query = new FilterFlowsheetQuery({
                filterId: filterId,
                filterVersionId: filterVersionId,
                sourceId: props.scenarioId,
                sourceType: ShareDefinitionSourceType.DoEExperiment
            });
            const filterResp = await client.getFlowsheetInfo(query);
            return filterResp;
        } catch (error) {
            setErrorMessage(`Experiment filter not found.`);
            processServerError(error, undefined, "An errror occurred while trying to get experiment filter.");
            return null;
        }
    }
    const getExperiment = async () => {

        try {
            setErrorMessage(undefined);
            const client = getScenariosClient();
            const scenarioResp = await client.getScenarioById(props.scenarioId);
            setScenario(scenarioResp);
            return scenarioResp;
        } catch (error) {
            console.log(`An error occured while loading experiment #${props.scenarioId}`, error);
            setErrorMessage(`An error occured while loading experiment.`);
            return undefined;
        }
    }

    const getSmallThumbnail = async (flowsheetUniqueId: string, flowsheetVersion: number) => {
        try {
            const client = getThumbnailsClient();
            const response = await client.getThumbnailSmall(flowsheetUniqueId, flowsheetVersion);
            if (response) {
                var reader = new FileReader();
                reader.readAsDataURL(response.data);
                reader.onloadend = function () {
                    var base64String = reader.result;
                    // console.log("thumbnail image", base64String);
                    setImage(base64String?.toString());
                }
                reader.onerror = () => {
                    console.log("error while reading thumbnail image");
                    setImage(undefined);
                };
            } else {
                setImage(undefined);
            }

        } catch (error) {
            setImage(undefined);
            console.log("getThumbnailUrl error", error);
        }


    }

    return <Card size="small" style={{ cursor: "pointer", marginBottom: "5px", borderRadius: "6px" }} onClick={() => { props.onClick() }}>
        {!isLoading && scenario && <CardHeader
            header={<b>{scenario.name}</b>}
        />}

        <CardPreview style={{ padding: "5px" }}>
            {!isLoading && !!errorMessage &&
                <Alert intent="error"
                    style={{ display: "flex", width: "250px", height: "fit-content", padding: 0, marginBottom: "5px" }}>
                    {errorMessage}
                </Alert>
            }
            {!isLoading && image && <img className="disable-visual-search" src={image} style={{ width: "252px", height: "fit-content" }} alt={`${scenario.name} flowsheet thumbnail`} />}
            {!isLoading && !image && <img className="disable-visual-search" style={{ width: "252px", height: "fit-content" }} src={thumbnail404} alt={`Thumbnail not found.`} />}
            {isLoading && <div style={{ padding: " 0px 5px" }}><Spinner size="medium" appearance="primary" label="Loading..." /></div>}
        </CardPreview>

    </Card>
}